import React, { useState, useRef } from "react";
import { Phone, Mail, Send, Check, Upload, X, Loader2, FileText, MapPin } from "lucide-react";
import { site, projectKinds, projectTypes, materialInterest, timelineOptions } from "../data/site";
import { toast } from "sonner";
import { Seo, breadcrumbLd } from "../components/Seo";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  first_name: "", last_name: "", phone: "", email: "", city: "", zip_code: "",
  project_kind: "", project_type: "", material_interest: "", timeline: "", details: "",
};

const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [consent, setConsent] = useState(false);
  const [files, setFiles] = useState([]); // {id, filename}
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const fileRef = useRef(null);

  const onChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  const onSelect = (name, value) => setForm((p) => ({ ...p, [name]: value }));

  const onFiles = async (e) => {
    const selected = Array.from(e.target.files || []);
    if (!selected.length) return;
    if (files.length + selected.length > 8) {
      toast.error("You can upload up to 8 files.");
      return;
    }
    setUploading(true);
    for (const file of selected) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is larger than 10MB.`);
        continue;
      }
      const fd = new FormData();
      fd.append("file", file);
      try {
        const res = await fetch(`${API}/uploads`, { method: "POST", body: fd });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || "Upload failed");
        }
        const data = await res.json();
        setFiles((prev) => [...prev, { id: data.id, filename: data.filename }]);
      } catch (err) {
        toast.error(err.message || "Upload failed");
      }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeFile = (id) => setFiles((prev) => prev.filter((f) => f.id !== id));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error("Please agree to be contacted about your estimate request.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/estimates`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          consent,
          file_ids: files.map((f) => f.id),
          company_website: honeypot,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Something went wrong. Please try again.");
      setSubmitted(true);
      toast.success("Request received — thank you!");
    } catch (err) {
      toast.error(err.message || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "h-12 bg-white border-neutral-300 focus:border-[#CD1422] rounded";

  if (submitted) {
    return (
      <div>
        <Seo title="Request a Free Estimate" description="Request a free estimate from Premier Granite LLC." path="/contact" noindex />
        <section className="section bg-white min-h-[70vh] flex items-center">
          <div className="container-custom max-w-xl text-center" data-testid="estimate-success">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#1A3D84] flex items-center justify-center">
              <Check size={30} className="text-white" />
            </div>
            <h1 className="text-3xl sm:text-4xl mb-4">Thank You</h1>
            <p className="text-neutral-600 leading-relaxed mb-8">
              Your project information has been received. A member of Premier Granite will follow up with you regarding next steps.
            </p>
            <a href={site.phoneHref} className="btn btn-primary"><Phone size={17} /> {site.phone}</a>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      <Seo
        title="Request a Free Estimate"
        description="Request a free estimate from Premier Granite LLC for granite, quartz and stone countertops, islands, vanities and commercial surfaces in the Dallas–Fort Worth area."
        path="/contact"
        jsonLd={breadcrumbLd([{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }])}
      />

      <section className="bg-[#1A3D84] py-16 md:py-20">
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="eyebrow eyebrow-light mb-4 inline-flex">Contact</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl text-white mb-4">Request a Free Estimate</h1>
            <p className="text-white/80 text-base md:text-lg leading-relaxed">
              Tell us about your project and a member of Premier Granite will follow up regarding next steps.
            </p>
          </div>
        </div>
      </section>

      <section className="section bg-white">
        <div className="container-custom grid lg:grid-cols-3 gap-12">
          {/* Form */}
          <div className="lg:col-span-2">
            <form onSubmit={onSubmit} className="space-y-6" data-testid="estimate-form">
              <div className="grid md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label className="text-sm text-neutral-700">First Name *</Label>
                  <Input name="first_name" value={form.first_name} onChange={onChange} required placeholder="First name" className={inputCls} data-testid="input-first-name" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-neutral-700">Last Name *</Label>
                  <Input name="last_name" value={form.last_name} onChange={onChange} required placeholder="Last name" className={inputCls} data-testid="input-last-name" />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label className="text-sm text-neutral-700">Phone *</Label>
                  <Input name="phone" type="tel" value={form.phone} onChange={onChange} required placeholder="(555) 555-5555" className={inputCls} data-testid="input-phone" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-neutral-700">Email *</Label>
                  <Input name="email" type="email" value={form.email} onChange={onChange} required placeholder="you@example.com" className={inputCls} data-testid="input-email" />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label className="text-sm text-neutral-700">City</Label>
                  <Input name="city" value={form.city} onChange={onChange} placeholder="Plano" className={inputCls} data-testid="input-city" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-neutral-700">ZIP Code</Label>
                  <Input name="zip_code" value={form.zip_code} onChange={onChange} placeholder="75024" className={inputCls} data-testid="input-zip" />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label className="text-sm text-neutral-700">Residential or Commercial</Label>
                  <Select value={form.project_kind} onValueChange={(v) => onSelect("project_kind", v)}>
                    <SelectTrigger className={inputCls} data-testid="select-project-kind"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{projectKinds.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-neutral-700">Project Type</Label>
                  <Select value={form.project_type} onValueChange={(v) => onSelect("project_type", v)}>
                    <SelectTrigger className={inputCls} data-testid="select-project-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent>{projectTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label className="text-sm text-neutral-700">Material Interest</Label>
                  <Select value={form.material_interest} onValueChange={(v) => onSelect("material_interest", v)}>
                    <SelectTrigger className={inputCls} data-testid="select-material"><SelectValue placeholder="Select material" /></SelectTrigger>
                    <SelectContent>{materialInterest.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-neutral-700">Approximate Timeline</Label>
                  <Select value={form.timeline} onValueChange={(v) => onSelect("timeline", v)}>
                    <SelectTrigger className={inputCls} data-testid="select-timeline"><SelectValue placeholder="Select timeline" /></SelectTrigger>
                    <SelectContent>{timelineOptions.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm text-neutral-700">Project Details</Label>
                <Textarea name="details" value={form.details} onChange={onChange} rows={4} placeholder="Tell us about your project, dimensions, and any questions..." className="bg-white border-neutral-300 focus:border-[#CD1422] rounded resize-none" data-testid="input-details" />
              </div>

              {/* Uploads */}
              <div className="space-y-2">
                <Label className="text-sm text-neutral-700">Upload Photos / Plans (optional)</Label>
                <div
                  onClick={() => fileRef.current?.click()}
                  className="border border-dashed border-neutral-300 rounded p-6 text-center hover:border-[#CD1422] transition-colors cursor-pointer bg-[#FAFAF9]"
                  data-testid="upload-dropzone"
                >
                  <Upload size={26} className="mx-auto text-neutral-400 mb-2" />
                  <p className="text-sm text-neutral-600">Click to upload photos of your space or plans</p>
                  <p className="text-xs text-neutral-400 mt-1">JPG, PNG, WEBP or PDF · up to 10MB each</p>
                  <input ref={fileRef} type="file" multiple accept=".jpg,.jpeg,.png,.webp,.pdf" className="hidden" onChange={onFiles} data-testid="file-input" />
                </div>
                {uploading && <p className="text-xs text-neutral-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Uploading…</p>}
                {files.length > 0 && (
                  <ul className="space-y-2 pt-2" data-testid="uploaded-files">
                    {files.map((f) => (
                      <li key={f.id} className="flex items-center justify-between gap-3 bg-[#FAFAF9] border border-neutral-200 rounded px-3 py-2">
                        <span className="flex items-center gap-2 text-sm text-neutral-700 truncate"><FileText size={15} className="text-[#1A3D84] shrink-0" /> {f.filename}</span>
                        <button type="button" onClick={() => removeFile(f.id)} className="text-neutral-400 hover:text-[#CD1422]" aria-label="Remove file"><X size={16} /></button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Honeypot (hidden) */}
              <input type="text" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} className="hidden" aria-hidden="true" />

              {/* Consent */}
              <label className="flex items-start gap-3 cursor-pointer" data-testid="consent-checkbox">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 w-4 h-4 accent-[#CD1422]" />
                <span className="text-sm text-neutral-600">
                  I agree that Premier Granite LLC may contact me regarding my estimate request.
                </span>
              </label>

              <button type="submit" disabled={submitting || uploading} className="btn btn-primary w-full sm:w-auto disabled:opacity-60" data-testid="submit-estimate">
                {submitting ? <><Loader2 size={17} className="animate-spin" /> Submitting…</> : <><Send size={16} /> Request Free Estimate</>}
              </button>
            </form>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="card-flat p-7 sticky top-24">
              <h2 className="text-2xl mb-6">Contact Premier Granite</h2>
              <div className="space-y-5">
                <a href={site.phoneHref} className="flex items-start gap-4 group">
                  <div className="w-10 h-10 rounded bg-[#1A3D84]/5 border border-neutral-200 flex items-center justify-center"><Phone size={16} className="text-[#CD1422]" /></div>
                  <div><p className="text-sm font-medium text-neutral-900 group-hover:text-[#CD1422] transition-colors">Call Us</p><p className="text-sm text-neutral-600">{site.phone}</p></div>
                </a>
                <a href={site.emailHref} className="flex items-start gap-4 group">
                  <div className="w-10 h-10 rounded bg-[#1A3D84]/5 border border-neutral-200 flex items-center justify-center"><Mail size={16} className="text-[#CD1422]" /></div>
                  <div><p className="text-sm font-medium text-neutral-900 group-hover:text-[#CD1422] transition-colors">Email</p><p className="text-sm text-neutral-600 break-all">{site.email}</p></div>
                </a>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded bg-[#1A3D84]/5 border border-neutral-200 flex items-center justify-center"><MapPin size={16} className="text-[#CD1422]" /></div>
                  <div><p className="text-sm font-medium text-neutral-900">Service Area</p><p className="text-sm text-neutral-600">{site.serviceArea}</p></div>
                </div>
              </div>
              <div className="border-t border-neutral-200 mt-6 pt-6">
                <p className="text-xs uppercase tracking-wider text-neutral-400 mb-2">Serving</p>
                <p className="text-sm text-neutral-600">{site.serviceCities.join(" · ")}</p>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default Contact;
